import React from 'react';

import Dialog, { DialogProps } from '@liquidweb-dev/undercurrent/Dialog';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import DialogTitle from '@liquidweb-dev/undercurrent/DialogTitle';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import type { LucideSvgProps } from '@liquidweb-dev/undercurrent/types/svgTypes';
import Typography from '@liquidweb-dev/undercurrent/Typography';

export interface DialogUTMBadge {
	icon: React.ComponentType<LucideSvgProps>;
	label: string;
}

export interface DialogUTMContent {
	title: string;
	description: React.ReactNode;
}

export interface DialogUTMProps extends Omit<DialogProps, 'onClose'> {
	image: string;
	badge: DialogUTMBadge;
	dialogContent: DialogUTMContent;
	dialogAction?: React.ReactNode;
	onClose: () => void;
}

const DialogUTM = ({
	image,
	badge,
	dialogContent,
	dialogAction,
	onClose,
	...dialogProps
}: DialogUTMProps) => {
	const BadgeIcon = badge.icon;

	return (
		<Dialog
			fullWidth
			maxWidth="md"
			sx={({ borderRadius }) => ({ borderRadius: borderRadius.roundedSm })}
			{...dialogProps}
		>
			<DialogTitle
				onClose={onClose}
				sx={{
					paddingBottom: 0,
					paddingTop: 100,
					'& .UcDialogTitle-close': { top: 16 },
				}}
			/>
			<DialogContent
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', md: 'row' },
					alignItems: 'center',
					padding: 300,
					gap: { xs: 300, md: 600 },
				}}
			>
				<Stack
					maxWidth={{ xs: '100%', md: '349px' }}
					sx={{
						boxSizing: 'content-box',
						border: ({ vars }) => `1px solid ${vars.palette.uc.border.weak}`,
						borderRadius: ({ borderRadius }) => borderRadius.roundedSm,
					}}
				>
					<img alt={dialogContent.title} src={image} />
				</Stack>
				<Stack gap={{ xs: 200, md: 400 }}>
					<Stack
						direction="row"
						alignItems="center"
						gap={50}
						sx={({ vars }) => ({ color: vars.palette.uc.success.text.main })}
					>
						<BadgeIcon />
						<Typography variant="label-lg" fontWeight={500}>
							{badge.label}
						</Typography>
					</Stack>
					<Stack
						gap={200}
						sx={({ vars }) => ({ color: vars.palette.uc.text.main })}
					>
						<Typography variant="title-sm" fontWeight={500}>
							{dialogContent.title}
						</Typography>
						{dialogContent.description}
					</Stack>
					{dialogAction}
				</Stack>
			</DialogContent>
		</Dialog>
	);
};

export default DialogUTM;
